import React, { ReactNode, CSSProperties, useMemo } from 'react';

import { COLORS, TYPOGRAPHY } from 'Components/foundation';
import type { IconName } from 'Components/foundation/icons';
import UButton from 'Components/unit/UButton/UButton';
import UIcon from 'Components/unit/UIcon/UIcon';

import styles from './SAlert.style';

type AlertDisplayStatus = 'success' | 'error' | 'warning';
type AlertDisplayPriority = 'low' | 'medium';

export type SAlertProps = {
  status: AlertDisplayStatus;
  priority?: AlertDisplayPriority;
  icon: IconName;
  title?: string;
  text?: string;
  isCallToAction?: boolean;
  onClose: () => void;
  onCancel?: () => void;
  onSuccess?: () => void;
  style?: CSSProperties;
  children?: ReactNode;
};

const colors = {
  low: {
    error: COLORS.ALERT.ERROR,
    success: COLORS.ALERT.SUCCESS,
    warning: COLORS.WARNING.HOVER,
  },
  medium: {
    error: COLORS.ERROR.DARKER,
    success: COLORS.SUCCESS.DARKER,
    warning: COLORS.WARNING.DARK,
  },
};

const secondaryColors = {
  low: {
    error: COLORS.ERROR.DEFAULT,
    success: COLORS.SUCCESS.DEFAULT,
    warning: COLORS.WARNING.HOVER,
  },
  medium: {
    error: COLORS.ERROR.DEFAULT,
    success: COLORS.SUCCESS.DEFAULT,
    warning: COLORS.WARNING.HOVER,
  },
};

const backgroundWrapper = {
  error: COLORS.ERROR.LIGHTER,
  success: COLORS.SUCCESS.LIGHTER,
  warning: COLORS.WARNING.LIGHTER,
};

const onSuccessColor = {
  error: styles.onError,
  success: styles.onSuccess,
  warning: styles.onWarning,
};

export const SAlert: React.FC<SAlertProps> = ({
  status,
  priority = 'low',
  icon,
  title,
  text,
  isCallToAction,
  onClose,
  onCancel,
  onSuccess,
  style,
  children,
}) => {
  const closeIconColor = priority === 'low' ? secondaryColors[priority][status] : COLORS.GREY_DARKER.DEFAULT;
  const successButtonStyle = priority === 'medium' ? onSuccessColor[status] : null;
  const shouldShowButtonsContainer = priority === 'medium' && isCallToAction && title;
  const colorStyle = {
    color: colors[priority][status],
    border: `1px solid ${secondaryColors[priority][status]}`,
    backgroundColor: priority === 'medium' ? backgroundWrapper[status] : COLORS.WHITE.DEFAULT,
  };

  const textStyle = useMemo(() => {
    const fontText = title ? TYPOGRAPHY.BODY4 : TYPOGRAPHY.BODY3;
    const styleDecision = title ? styles.textMedium : styles.textLow;

    if (priority === 'medium') {
      return { ...fontText, ...styleDecision };
    }

    return { ...fontText, ...styles.textLow };
  }, [priority, status, title]);

  return (
    <div style={{ ...styles.wrapper, ...colorStyle, ...style }}>
      <div>
        <UIcon name={icon} size={20} color={colors[priority][status]} style={styles.icon} />
        <div onClick={onClose} style={styles.closeIcon}>
          <UIcon name={'close'} size={10} color={closeIconColor} />
        </div>
      </div>
      {title && <span style={styles.title}>{title}</span>}
      {text && <span style={textStyle}>{text}</span>}
      {children && <div>{children}</div>}

      {shouldShowButtonsContainer && (
        <div style={styles.buttonsContainer}>
          <UButton type="standard" text="Yes" onClick={onSuccess} style={successButtonStyle} />
          <UButton type="standard" text="No" onClick={onCancel} style={styles.cancelButton} />
        </div>
      )}
    </div>
  );
};

export default SAlert;
